import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import type { DTOInviteFromSwipeAzarCard } from 'src/apis/dto/DTOInviteFromSwipeAzar';
import GenericButton from 'src/components/GenericButton';
import ModalLayoutBase, { ModalFooter as ModalFooterBase, MobileModalType } from 'src/components/ModalLayout';
import ResizingText from 'src/components/ResizingText';
import { useBulkInvite } from 'src/hooks/useBulkInvite';
import useCssMediaDevice from 'src/hooks/useCssMediaDevice';
import { remoteConfigAtom } from 'src/stores/auth/atoms';
import { handleDisconnectAtom } from 'src/stores/match/atoms';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { showToastAtom } from 'src/stores/toast/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { ToastType } from 'src/types/Toast';
import { pushEvent } from 'src/utils/event-gateway';
import InviteUserList from './InviteUserList';
interface Props {
  cards: DTOInviteFromSwipeAzarCard[];
}
const ModalLayout = styled(ModalLayoutBase)`
  width: 500px;
  height: 600px;
`;
const SkipBar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 20px 24px 8px;
  flex-shrink: 0;
`;
const SkipButton = styled.button`
  all: unset;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.content8};

  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 24px 8px 24px;
  flex-shrink: 0;
`;
const Title = styled(ResizingText)`
  margin: 0;
  font-size: 30px;
  font-weight: 700;
  line-height: 30px;
  color: ${({
  theme
}) => theme.color.content12};

  ${({
  theme
}) => theme.screenSize.tablet} {
    font-size: 36px;
    line-height: 39px;
  }
`;
const Description = styled(ResizingText)`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: ${({
  theme
}) => theme.color.content9};

  ${({
  theme
}) => theme.screenSize.tablet} {
    font-size: 16px;
    line-height: 19px;
    color: ${({
  theme
}) => theme.color.content11};
  }
`;
const InviteButton = styled(GenericButton)`
  width: 100%;
`;
const ModalFooter = styled(ModalFooterBase)`
  gap: 12px;
`;
const fontSizes = {
  title: {
    mobile: 30,
    tablet: 30,
    desktop: 24,
    maxLines: {
      mobile: 3,
      tablet: 3,
      desktop: 2
    }
  },
  description: {
    mobile: 14,
    tablet: 14,
    desktop: 12,
    maxLines: {
      mobile: 4,
      tablet: 4,
      desktop: 3
    }
  }
};
export default function InviteFromSwipeAzarModal({
  cards
}: Props) {
  const closeModal = useSetAtom(closeModalAtom);
  const handleDisconnect = useSetAtom(handleDisconnectAtom);
  const showToast = useSetAtom(showToastAtom);
  const {
    mutate: bulkInvite
  } = useBulkInvite();
  const {
    t
  } = useTranslation('translations');
  const [users, setUsers] = useState<DTOInviteFromSwipeAzarCard[]>(cards);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const device = useCssMediaDevice() || 'desktop';
  // IFSA는 nonpayer_punchy 실험군에게만 노출되므로, remote config 미수신 시에도 nonpayer_punchy로 fallback
  const inviteFromSwipeAzarUserType = useAtomValue(remoteConfigAtom)?.inviteFromSwipeAzarDescText ?? 'nonpayer_punchy';
  const isPunchy = inviteFromSwipeAzarUserType === 'nonpayer_punchy';
  const isPayer = inviteFromSwipeAzarUserType === 'payer';
  const selectedCount = selectedIds.size;
  const titleKey = isPunchy ? 'manual_invite_list_title_punchy' : 'manual_invite_list_title';
  const descKey = isPayer ? 'manual_invite_list_desc_payer' : isPunchy ? 'manual_invite_list_desc_punchy' : 'manual_invite_list_desc';
  const mainButtonKey = isPunchy ? 'manual_invite_list_main_button_punchy' : 'manual_invite_list_main_button';
  const getSubButtonKey = (count: number) => {
    if (isPunchy) {
      return count === 1 ? 'manual_invite_list_sub_button_single_punchy' : 'manual_invite_list_sub_button_plural_punchy';
    }
    return count === 1 ? 'manual_invite_list_sub_button_single' : 'manual_invite_list_sub_button_plural';
  };
  const handleClose = useCallback(() => {
    closeModal(ModalType.INVITE_FROM_SWIPE_AZAR);
    handleDisconnect({
      reconnect: true,
      finisher: true
    });
  }, [closeModal, handleDisconnect]);
  useEffect(() => {
    if (users.length === 0) handleClose();
  }, [users.length, handleClose]);
  const handleSkip = () => {
    pushEvent({
      eventType: EVENT_TYPE.INVITE_FROM_SWIPE_AZAR,
      eventName: EVENT_NAME.SWIPE_INVITE__CLICK_SKIP,
      eventParams: {
        action_category: 'click',
        card_ids: users.map(u => u.cardId),
        page: 'swipe_invite',
        tab: 'mirror',
        target: 'skip'
      }
    });
    handleClose();
  };
  const handleInvite = (targetIds: string[], type: 'all' | 'selected') => {
    pushEvent({
      eventType: EVENT_TYPE.INVITE_FROM_SWIPE_AZAR,
      eventName: type === 'all' ? EVENT_NAME.SWIPE_INVITE__CLICK_CONTINUE_WITH_ALL_USERS : EVENT_NAME.SWIPE_INVITE__CLICK_CONTINUE_WITH_SELECTED_USERS,
      eventParams: {
        action_category: 'click',
        card_ids: targetIds,
        page: 'swipe_invite',
        tab: 'mirror',
        target: type === 'all' ? 'continue_with_all_users' : 'continue_with_selected_users'
      }
    });
    bulkInvite(targetIds);
    showToast({
      type: ToastType.SUCCESS,
      message: t(targetIds.length === 1 ? 'manual_invite_complete_alert_single' : 'manual_invite_complete_alert_plural', {
        count: targetIds.length
      })
    });
    handleClose();
  };
  const handleToggleSelect = (cardId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  };
  const handleRemoveUser = (cardId: string) => {
    pushEvent({
      eventType: EVENT_TYPE.INVITE_FROM_SWIPE_AZAR,
      eventName: EVENT_NAME.SWIPE_INVITE__CLICK_DELETE,
      eventParams: {
        action_category: device === 'desktop' ? 'click' : 'swipe',
        card_id: cardId,
        page: 'swipe_invite',
        tab: 'mirror',
        target: 'delete'
      }
    });
    setUsers(prev => prev.filter(u => u.cardId !== cardId));
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(cardId);
      return next;
    });
  };
  useEffect(() => {
    // 모달이 오픈된 상태에서 매치종료, 다음매치 키보드이벤트 동작하지 않도록 처리
    const stopPropagation = (e: KeyboardEvent) => e.stopPropagation();
    window.addEventListener('keyup', stopPropagation, {
      capture: true
    });
    return () => window.removeEventListener('keyup', stopPropagation, {
      capture: true
    });
  }, []);
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.INVITE_FROM_SWIPE_AZAR,
      eventName: EVENT_NAME.SWIPE_INVITE__IMPRESSION_INVITE,
      eventParams: {
        action_category: 'impression',
        card_ids: cards.map(c => c.cardId),
        page: 'swipe_invite',
        tab: 'mirror',
        target: 'swipe_invite'
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <ModalLayout mobileType={MobileModalType.SCREEN_MODAL} mobileNavButtonType={ModalNavButtonType.NONE} isDim closeWhenClickOutside={false} closeWithESC={false} data-testid='InviteFromSwipeAzarModal' data-sentry-element="ModalLayout" data-sentry-component="InviteFromSwipeAzarModal" data-sentry-source-file="index.tsx">
      <SkipBar data-sentry-element="SkipBar" data-sentry-source-file="index.tsx">
        <SkipButton onClick={handleSkip} data-testid='InviteFromSwipeAzarModal-skip' data-sentry-element="SkipButton" data-sentry-source-file="index.tsx">
          {t('COMMON__SKIP')}
        </SkipButton>
      </SkipBar>

      <TitleSection data-sentry-element="TitleSection" data-sentry-source-file="index.tsx">
        <Title minFontSize={fontSizes.title[device]} maxLines={fontSizes.title.maxLines[device]} data-sentry-element="Title" data-sentry-source-file="index.tsx">
          {t(titleKey)}
        </Title>
        <Description minFontSize={fontSizes.description[device]} maxLines={fontSizes.description.maxLines[device]} data-sentry-element="Description" data-sentry-source-file="index.tsx">
          {t(descKey)}
        </Description>
      </TitleSection>

      <InviteUserList users={users} selectedIds={selectedIds} onToggleSelect={handleToggleSelect} onRemoveUser={handleRemoveUser} data-sentry-element="InviteUserList" data-sentry-source-file="index.tsx" />

      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="index.tsx">
        {selectedCount > 0 && selectedCount < users.length && <InviteButton variant='outline' size='large' onClick={() => handleInvite([...selectedIds], 'selected')} data-testid='InviteFromSwipeAzarModal-select-invite'>
            {t(getSubButtonKey(selectedCount), {
          count: selectedCount
        })}
          </InviteButton>}
        <InviteButton variant='primary' size='large' onClick={() => handleInvite(users.map(u => u.cardId), 'all')} data-testid='InviteFromSwipeAzarModal-all-invite' data-sentry-element="InviteButton" data-sentry-source-file="index.tsx">
          {t(mainButtonKey)}
        </InviteButton>
      </ModalFooter>
    </ModalLayout>;
}